import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';

import { TimeUnit } from 'webapp/enums/enums';

import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormCustomPeriodPickerComponent from 'webapp/components/form/custom-period-picker';
import FormInputIntegerComponent from 'webapp/components/form/input-integer';
import type { Args as BaseArgs } from 'webapp/components/form/range/list-item-base';
import FormRangeListItemBaseComponent from 'webapp/components/form/range/list-item-base';
import FormSelectComponent from 'webapp/components/form/select';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import type MetricLimitationCompare from 'webapp/models-custom/metric-limitation/compare';

export interface Args extends BaseArgs {
	model: DsCompare;
}

export default class FormCompareListItemComponent extends FormRangeListItemBaseComponent<Args> {
	customDateFormat = DateRange.customDateFormat;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.checkAndResetExtraProps();
	}

	get text() {
		return this.args.model.displayTitle;
	}

	get customDateRange() {
		return this.args.model.customDateRangeCM;
	}

	get limitationForCurrentSelection(): MetricLimitationCompare {
		return this.getPriorLimitationForTimeUnit(
			this.args.model.timeUnit as TimeUnit,
		) as MetricLimitationCompare;
	}

	override get maxMultiplier() {
		return this.limitationForCurrentSelection.rawData?.maxMultiplier;
	}

	get timeUnits() {
		const granulations = DateRange.baseGranulations.filter(
			(item) => item.id !== TimeUnit.TOTAL && item.id !== TimeUnit.HOUR,
		);

		let timeUnits;
		if (this.args.model.isPriorOffset) {
			timeUnits = granulations.map((timeUnit) => {
				const limitation = this.getPriorLimitationForTimeUnit(
					timeUnit.id,
				) as MetricLimitationCompare;

				let tooltipText = '';

				// if the unit is disabled, show that on tooltip in dropdown
				if (limitation.disabled) {
					if (limitation.infoText) {
						const iconName = limitation.isPaidLimitation ? 'Lock' : 'Question';
						const iconClass = limitation.isPaidLimitation ? ' class="green"' : '';
						tooltipText = `<ui-icon name="${iconName}"${iconClass} title="${limitation.infoText}"></ui-icon>`;
					}
				}

				return {
					value: timeUnit.id,
					label: `${timeUnit.title.toLowerCase()}s ago`,
					label3: tooltipText,
					disabled: limitation.disabled,
				};
			});
		} else {
			timeUnits = granulations.map((timeUnit) => {
				return {
					value: timeUnit.id,
					label: timeUnit.title,
					disabled: false,
				};
			});
		}

		if (this.args.hideUnsupported) {
			timeUnits = timeUnits.filter((item) => !item.disabled);
		}

		return timeUnits;
	}

	getPriorLimitationForTimeUnit(timeUnit: TimeUnit) {
		return (
			(this.args.model.limitations || []).find(
				(item) => item.id === DsCompare.timeUnitToTypeId(timeUnit),
			) || {}
		);
	}

	@action
	checkAndResetExtraProps() {
		if (this.showExtra) {
			// only for selected
			if (this.args.model.isPriorOffset) {
				this.checkSelectedTimeUnit();
			}
		}
	}

	// timeUnit switching if selected is not supported
	@action
	checkSelectedTimeUnit() {
		const compareTimeUnit = this.args.model.timeUnit,
			selectedTimeUnit = compareTimeUnit
				? this.timeUnits.find((item) => item.value === compareTimeUnit)
				: null;
		if (!selectedTimeUnit || selectedTimeUnit.disabled) {
			const firstAvailable = this.timeUnits.find((item) => !item.disabled)?.value;
			if (firstAvailable) {
				this.args.model.timeUnit = firstAvailable;
			}
		}
	}

	@action
	onOffsetChange(offset: number) {
		this.args.model.positiveOffset = offset;
	}

	@action
	onTimeUnitChange(timeUnit: TimeUnit) {
		this.args.model.timeUnit = timeUnit;

		if (this.args.model.isPriorOffset) {
			this.args.model.typeId = DsCompare.timeUnitToTypeId(timeUnit);
		}
	}

	@action
	onRollingChange(isRolling: boolean) {
		this.customDateRange.isRolling = isRolling;
	}

	@action
	onCustomPeriodChange(startDate: string, endDate: string, isRolling: boolean) {
		this.customDateRange.rangeStartDate = startDate;
		this.customDateRange.rangeEndDate = endDate;

		this.onRollingChange(isRolling);
	}

	@action
	confirmAndClose() {
		this.args.onSelect(this.args.model);
	}
	<template>
		<li
			class="item-ct
				{{if @isSelected 'selected'}}
				{{if @model.isDisabled 'disabled'}}
				{{this.limitationInfo.className}}
				{{if @model.isCustom 'is-datepicker'}}
				{{if @model.isPriorOffset 'is-prior-offset'}}"
			{{didUpdate this.checkAndResetExtraProps @isSelected}}
			{{didUpdate this.checkSelectedTimeUnit @model.timeUnit}}
		>
			<div class="item">
				<a class="item-title" {{on "click" this.select}}>
					{{#if @isSelected}}
						<CommonUiIconComponent
							@name="CheckboxTick"
							@size="xs"
							@class="selected-icon"
						/>
					{{/if}}
					{{this.text}}

					{{#if this.showInfoTextAtTitle}}
						<ToolTipWithIconComponent
							@text={{this.limitationInfo.text}}
							@maxWidth={{250}}
							@iconName={{this.limitationInfo.icon}}
							@theme={{@theme}}
						/>
					{{/if}}
				</a>
				{{#if this.showExtra}}
					<div class="item-extra">
						{{#if @model.isPriorOffset}}
							<div class="extra-field-multiplier">
								<FormInputIntegerComponent
									@name="offset"
									@onChange={{this.onOffsetChange}}
									@showWarningOnMaxChange={{false}}
									@value={{@model.positiveOffset}}
									@min={{1}}
									@max={{this.maxMultiplier}}
									@maxlength={{4}}
									@size="tiny"
									@theme={{@theme}}
								/>
							</div>
							<div class="extra-field-unit">
								<FormSelectComponent
									name="timeUnit"
									@selected={{@model.timeUnit}}
									@onChange={{this.onTimeUnitChange}}
									@options={{this.timeUnits}}
									@tooltipOnSelectionFromProperty=""
									@isRequired={{true}}
									@size="tiny"
									@theme={{@theme}}
								/>
								{{#if this.maxMultiplier}}
									<ToolTipWithIconComponent
										@text={{this.maxMultiplierWarningText}}
										@maxWidth={{250}}
										@iconName="Question"
										@iconSize="md"
										@theme={{@theme}}
									/>
								{{/if}}
							</div>
						{{/if}}
						{{#if @model.isCustom}}
							<div class="extra-datepicker">
								<FormCustomPeriodPickerComponent
									@format={{this.customDateFormat}}
									@startValue={{this.customDateRange.rangeStartDate}}
									@endValue={{this.customDateRange.rangeEndDate}}
									@isRolling={{this.customDateRange.isRolling}}
									@isRollingCheckboxDisabled={{false}}
									@isRollingCheckboxHidden={{false}}
									@onChange={{this.onCustomPeriodChange}}
									@theme={{@theme}}
									@size="tiny"
								/>
							</div>
							<div class="extra-checkbox">
								<FormCheckboxComponent
									@name="isRolling"
									@selected={{this.customDateRange.isRolling}}
									@onChange={{this.onRollingChange}}
									@label="Rolling range"
									@theme="blue2"
									@size="small"
								/>
							</div>
						{{/if}}
						<a class="apply" {{on "click" this.confirmAndClose}}>OK</a>
					</div>
				{{/if}}
			</div>
		</li>
	</template>
}
